// src/services/api.js
import axios from "axios";

/* =========================
   AXIOS INSTANCE
========================= */

const BASE_URL = process.env.REACT_APP_API || "/api/v1";

const api = axios.create({
  baseURL: BASE_URL,
});

// Attach token automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("app_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* =========================
    HANDLE SESSION EXPIRY
========================= */

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      console.warn("Session expired or unauthorized");

      localStorage.removeItem("app_token");
      localStorage.removeItem("app_user");

      // notify UI
      window.dispatchEvent(new Event("session-expired"));

      // redirect to login if not already there
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

/* =========================
   EXISTING APIs (UNCHANGED)
========================= */

export const getSessions = async () => {
  const res = await api.get("/sessions");
  return res.data;
};

export const getPayments = async () => {
  const res = await api.get("/payments");
  return res.data;
};

export const getTickets = async () => {
  const res = await api.get("/tickets");
  return res.data;
};

/* =========================
   HIERARCHY
========================= */

export const getJimbos = async () => {
  const res = await api.get("/jimbo");
  return res.data;
};

export const createJimbo = async (payload) => {
  const res = await api.post("/jimbo", payload);
  return res.data;
};

export const updateJimbo = (id, data) =>
  api.put(`/jimbo/${id}`, data);


export const getParokia = (jimboId) =>
  api.get("/parokia", { params: { jimboId } });

export const createParokia = (data) =>
  api.post("/parokia", data);

export const createFacilityId = (parokiaId) =>
  api.post("/parokia/generate-facility-id", { parokiaId });

export const updateParokia = (parokiaId, data) =>
  api.put(`/parokia/${parokiaId}`, data);

export const getParokiaByJimbo = async (jimboId) => {
  const res = await api.get("/parokia", {
    params: { jimboId },
  });
  return res.data;
};

export const getKanda = (parokiaId) =>
  api.get("/kanda", { params: { parokiaId } });

export const createKanda = (data) =>
  api.post("/kanda", data);

export const getJumuia = (parokiaId, kandaId) =>
  api.get("/jumuia", { params: { parokiaId, kandaId } });

export const createJumuia = (data) =>
  api.post("/jumuia", data);

export const updateJumuia = (id, data) =>
  api.put(`/jumuia/${id}`, data);
/* =========================
   MEMBERS
========================= */

export const getMembers = () =>
  api.get("/members"); // backend already scopes by parokia

export const createMember = (data) =>
  api.post("/members", data);

export const createMemberBulkUpload = (formData) =>
  api.post("/members/bulk-upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  
export const updateMember = (memberId, payload) =>
  api.put(`/members/${memberId}`, payload);

/* =========================
   TRANSACTIONS (NEW FIX)
========================= */

// List transactions
export const getTransactions = () =>
  api.get("/transactions");

// Create transaction
export const createTransaction = (data) =>
  api.post("/transactions", data);

// Contribution types (for modal dropdown)
export const getContributionTypes = () =>
  api.get("/contribution-types");

/* =========================
   SMS MANAGEMENT
========================= */

// Templates
export const getSmsTemplates = () =>
  api.get("/sms/templates");

export const saveSmsTemplate = (data) =>
  api.post("/sms/templates", data);

// Get template by contribution type
export const getSmsTemplateByType = (type) =>
  api.get("/sms/template-by-type", {
    params: { type },
  });

// Send SMS manually
export const sendSms = (data) =>
  api.post("/sms/send", data);

// Send Bulk SMS manually
export const sendBulkSms = (data) =>
  api.post("/sms/bulk", data);

export const getSmsJob = (jobId) =>
   api.get( `/sms/jobs/${jobId}`);

// SMS balance
export const getSmsBalance = (data) =>
  api.get("/sms/balance", data);

export const getSmsSubscriptions = () => api.get("/sms/subscriptions");
export const buySmsSubscription = (payload) => api.post("/sms/subscriptions", payload);
//export const sendBulkSms = (payload) => api.post("/sms/bulk", payload);

// Logs
export const getSmsLogs = () =>
  api.get("/sms/logs");

export const toggleSmsTemplate = (templateId) =>
  api.put(`/sms/templates/${templateId}/toggle`);
/* =========================
   ADMIN (SYSTEM ONLY)
========================= */

export const createParokiaAdmin = (data) =>
  api.post("/admin/create-parokia-admin", data);

export const getParokiaMembersAdmin = (parokiaId) =>
  api.get(`/admin/parokia/${parokiaId}/members`);

export const getParokiaAdmins = (parokiaId) =>
  api.get(`/admin/parokia/${parokiaId}/admins`);

// export const getParokiaHierarchy = () =>
//   api.get("/admin/parokia");
export const listParokiaWithAdmins = () =>
  api.get("/admin/parokia");

export const listCashiersByParokia = () =>
  api.get("/admin/list-cashier");



export const assignParokiaAdmin = (data) =>
  api.post("/admin/create-parokia-admin", data);

export const assignParokiaCashier = (data) =>
  api.post("/admin/create-parokia-cashier", data);

export const resetParokiaAdminPassword = (adminId, password) =>
  api.post("/admin/reset-parokia-admin-password", {
    adminId,
    password,
  });

export const getMyParokia = () =>
  api.get("/parokia/self");

/* CONTRIBUTION TYPES */
export const listContributionTypes = () =>
  api.get("/contribution-types");

export const createContributionType = (data) =>
  api.post("/contribution-types", data);

export const updateContributionType = (id, data) =>
  api.put(`/contribution-types/${id}`, data);

/* REPORTS */
// export const getDashboardSummary = () => 
//   api.get("/reports/dashboard-summary");
// export const getMonthlyCollection = () => 
//   api.get("/reports/monthly-collection");
// export const getChannelSummary = () => 
//   api.get("/reports/channel-summary");
// export const getContributionTypeSummary = () => 
//   api.get("/reports/contribution-type-summary");
// export const getTopJumuia = () => 
//   api.get("/reports/top-jumuia");
// export const getRecentTransactions = () => 
//   api.get("/reports/recent-transactions");

// export const getReportLookups = () => 
//   api.get("/reports/lookups");

// export const getTransactionReport = (params = {}) =>
//   api.get("/reports/transactions", { params });
export const getDashboardSummary = (params = {}) =>
  api.get("/reports/dashboard/summary", { params });

export const getMonthlyCollection = (params = {}) =>
  api.get("/reports/dashboard/monthly-collection", { params });

export const getRecentTransactions = () =>
  api.get("/reports/dashboard/recent-transactions");

export const getChannelSummary = (params = {}) =>
  api.get("/reports/dashboard/channel-summary", { params });

export const getContributionTypeSummary = (params = {}) =>
  api.get("/reports/dashboard/contribution-type-summary", { params });

export const getTopJumuia = (params = {}) =>
  api.get("/reports/dashboard/top-jumuia", { params });

export const getReportLookups = () =>
  api.get("/reports/lookups");

export const getTransactionReport = (params = {}) =>
  api.get("/reports/transactions", { params });

export const getMemberRegisterReport = (params = {}) =>
  api.get("/reports/members", { params });

export const getContributionSummaryReport = (params = {}) =>
  api.get("/reports/contribution-summary", { params });

export const getChannelSummaryReport = (params = {}) =>
  api.get("/reports/channel-summary", { params });

export const getCashierSummaryReport = (params = {}) =>
  api.get("/reports/cashier-summary", { params });

export const approveSmsSubscription = (id) =>
  api.patch(`/sms/subscriptions/${id}/approve`);

export const rejectSmsSubscription = (id, data) =>
  api.patch(`/sms/subscriptions/${id}/reject`, data);

export const getTransactionRequests = () =>
  api.get("/transactions/requests");

// export const requestTransactionEdit = (transactionId, payload) =>
//   api.post(`/transactions/${transactionId}/request-edit`, payload);

// export const requestTransactionDelete = (transactionId, payload) =>
//   api.post(`/transactions/${transactionId}/request-delete`, payload);

// export const approveTransactionRequest = (requestId) =>
//   api.post(`/transactions/requests/${requestId}/approve`);

// export const rejectTransactionRequest = (requestId) =>
//   api.post(`/transactions/requests/${requestId}/reject`);

export const getTransactionApprovals = () =>
  api.get("/transactions/approvals");

export const submitTransactionEditApproval = (transactionId, payload) =>
  api.post(`/transactions/${transactionId}/submit-edit-approval`, payload);

export const submitTransactionDeleteApproval = (transactionId, payload) =>
  api.post(`/transactions/${transactionId}/submit-delete-approval`, payload);

export const approveTransactionAction = (requestId, payload) =>
  api.post(`/transactions/approvals/${requestId}/approve`, payload);

export const rejectTransactionAction = (requestId, payload) =>
  api.post(`/transactions/approvals/${requestId}/reject`, payload);

export default api;
